import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contaApi } from '../../services/api';
import { useClienteAuth } from '../../contexts/ClienteAuthContext';
import { KpiCard, Loading, EmptyState } from '../../components/ui';
import { formatarMoeda } from '../../lib/format';
import { CartaoPedido, BotaoPagar } from './CartaoPedido';
import { podePagarOnline } from './statusLoja';
import { Package, Truck, Wallet, ArrowRight, ShoppingBag } from 'lucide-react';

export function ContaPainelPage() {
  const { cliente } = useClienteAuth();

  const { data: pedidos, isLoading } = useQuery({
    queryKey: ['conta-pedidos'],
    queryFn: () => contaApi.pedidos().then(r => r.data as any[]),
  });

  if (isLoading) return <Loading />;

  const lista = pedidos || [];
  const emAndamento = lista.filter(p => ['PAGO', 'EM_PRODUCAO', 'PRONTO'].includes(p.status));
  const pendentesPagamento = lista.filter(p => podePagarOnline(p));
  const valorPendente = pendentesPagamento.reduce((s, p) => s + p.valorTotal, 0);
  const recentes = lista.slice(0, 3);
  const primeiroNome = cliente?.nome?.split(' ')[0] || '';

  return (
    <div className="space-y-8 animate-fade-up">
      {/* Saudação */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink-950 tracking-tight">
            Olá, {primeiroNome}! 👋
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Aqui você acompanha seus pedidos, paga online e faz novos pedidos.
          </p>
        </div>
        <Link to="/loja" className="btn-primary !px-5 shrink-0">
          <ShoppingBag className="w-4 h-4" /> Fazer novo pedido
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard titulo="Pedidos realizados" valor={lista.length} icon={Package} tom="brand" />
        <KpiCard titulo="Em andamento" valor={emAndamento.length} icon={Truck} tom="blue" />
        <KpiCard
          titulo="Pagamentos pendentes"
          valor={pendentesPagamento.length}
          icon={Wallet}
          tom={pendentesPagamento.length > 0 ? 'amber' : 'green'}
          detalhe={valorPendente > 0 ? formatarMoeda(valorPendente) : 'Tudo em dia'}
        />
      </div>

      {/* Pagamentos pendentes */}
      {pendentesPagamento.length > 0 && (
        <section>
          <h2 className="card-title mb-4">Pagamentos pendentes</h2>
          <div className="card !p-0 divide-y divide-ink-100">
            {pendentesPagamento.map(p => (
              <div key={p.id} className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4">
                <div>
                  <p className="text-sm font-bold text-ink-950">Pedido #{p.numero}</p>
                  <p className="text-xs text-ink-400 mt-0.5">
                    {p.itens.reduce((s: number, i: any) => s + i.quantidade, 0)} item(ns) · {formatarMoeda(p.valorTotal)}
                  </p>
                </div>
                <div className="ml-auto">
                  <BotaoPagar pedidoId={p.id} valor={p.valorTotal} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pedidos recentes */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="card-title">Pedidos recentes</h2>
          {lista.length > 3 && (
            <Link to="/loja/conta/pedidos" className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:text-brand-700">
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {lista.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={Package}
              titulo="Você ainda não fez nenhum pedido"
              descricao="Que tal começar agora? Personalize sua camiseta na loja."
            />
            <div className="flex justify-center pb-6 -mt-4">
              <Link to="/loja" className="btn-primary !px-5">
                <ShoppingBag className="w-4 h-4" /> Ir para a loja
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {recentes.map(p => <CartaoPedido key={p.id} pedido={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
